import React from 'react'
import { Box, Typography } from '@mui/material'
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import { getTicketStatusConfig } from '../../shared/utilities/statusConfig'
import { useThemeMode } from '../../shared/hooks'
import { dayjsFormat } from '../../shared/utilities/util'

const TicketRow = ({
  id,
  subject,
  update,
  status,
  handleOpen,
  onClick,
  replyCount = 0,
  companyName = '',
  hasUnseenReply = false,
}) => {
  const { isDark } = useThemeMode()
  const config = getTicketStatusConfig(status)
  const dotColor = isDark ? config.darkColor : config.color

  const labelSx = {
    fontSize: '11px',
    fontWeight: 700,
    color: isDark ? '#64748b' : '#94a3b8',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    lineHeight: 1.4,
    mb: 0.25,
  }

  const valueSx = {
    fontSize: '14px',
    fontWeight: 500,
    color: 'text.primary',
    lineHeight: 1.4,
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
        borderLeft: `3px solid ${dotColor}40`,
        borderRadius: '12px',
        bgcolor: isDark ? 'transparent' : 'background.paper',
        '&:hover': {
          backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
          borderLeftColor: dotColor,
        },
      }}
      // Rows were plain clickable divs — unreachable by keyboard.
      role="button"
      tabIndex={0}
      aria-label={`Open ticket ${id}${hasUnseenReply ? ', has unread reply' : ''}`}
      onClick={() => {
        onClick()
        handleOpen()
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onClick()
          handleOpen()
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 2, md: 4 },
          px: { xs: 2, md: 3 },
          py: 2,
          width: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            bgcolor: isDark ? 'rgba(59,130,246,0.1)' : '#eff6ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <ChatOutlinedIcon sx={{ fontSize: 20, color: isDark ? '#60a5fa' : '#3b82f6' }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 80 }}>
          <Typography sx={labelSx}>Ticket</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ ...valueSx, fontWeight: 600 }}>{id}</Typography>
            {hasUnseenReply && (
              <Box
                title="Unread customer reply"
                sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2563eb', flexShrink: 0 }}
              />
            )}
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 80 }}>
          <Typography sx={labelSx}>Company</Typography>
          <Typography sx={{ ...valueSx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {companyName || '--'}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 80 }}>
          <Typography sx={labelSx}>Subject</Typography>
          <Typography sx={{ ...valueSx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {subject}
          </Typography>
        </Box>

        <Box sx={{ flex: 1.5, minWidth: 140 }}>
          <Typography sx={labelSx}>Last Update</Typography>
          <Typography sx={valueSx}>{dayjsFormat(update, 'MMM DD, YYYY h:mm A')}</Typography>
        </Box>

        <Box sx={{ minWidth: 60, flexShrink: 0 }}>
          <Typography sx={labelSx}>Replies</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography sx={valueSx}>{replyCount}</Typography>
          </Box>
        </Box>

        <Box sx={{ minWidth: 80, flexShrink: 0, textAlign: 'right' }}>
          <Typography sx={labelSx}>Status</Typography>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
              borderRadius: '6px',
              px: 1,
              py: 0.25,
            }}
          >
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: dotColor }} />
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: dotColor }}>{config.label}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default TicketRow
